/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../modals/Modal';
import { ActivityIndicator, Pressable, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CameraView, type BarcodeScanningResult, useCameraPermissions } from 'expo-camera';
import { spacing, ticketColors, ticketFonts, typography } from '../theme/public-api';
import { useModalBackdropPadding } from '../ui/hooks-ui/useModalBackdropPadding';
import {
  BARCODE_SCAN_LOCK_MS,
  BARCODE_INTER_READ_LOCK_MS,
  BARCODE_MULTI_SCAN_MAX_MISMATCHES,
} from '../product-search/productBarcodeInput';
import { configureWebBarcodeScanner } from './webBarcodeScannerSetup';
import { processScanRead, type MultiScanState } from './barcodeMultiScan';

configureWebBarcodeScanner();

const SCAN_LOCK_MS = BARCODE_SCAN_LOCK_MS;
const INTER_READ_LOCK_MS = BARCODE_INTER_READ_LOCK_MS;
const MAX_MISMATCHES = BARCODE_MULTI_SCAN_MAX_MISMATCHES;

const barcodeTypes = ['ean13', 'ean8', 'upc_a', 'upc_e', 'code128', 'code39', 'itf14'] as const;

type BarcodeScannerModalProps = {
  visible: boolean;
  title?: string;
  subtitle?: string;
  onClose: () => void;
  onCodeScanned: (code: string) => void;
  /** Cuando es true, muestra el código escaneado con botones Confirmar/Re-scannear antes de llamar onCodeScanned. */
  requireConfirmation?: boolean;
};

export const BarcodeScannerModal = ({
  visible,
  title = 'Leer código',
  subtitle = 'Poné el código frente a la cámara.',
  onClose,
  onCodeScanned,
  requireConfirmation = false,
}: BarcodeScannerModalProps) => {
  const modalBackdropPadding = useModalBackdropPadding();
  const { width: winW, height: winH } = useWindowDimensions();
  const isMobile = winW < 768;

  const handleDeferredClose = useCallback(() => {
    onClose();
  }, [onClose]);
  const insets = useSafeAreaInsets();
  const cameraFrameHeight = Math.min(360, Math.round(winH * 0.4));
  const stateCardMinHeight = Math.min(320, Math.round(winH * 0.42));
  const [permission, requestPermission] = useCameraPermissions();

  // Lock post-aceptación (evita re-trigger del mismo código)
  const [scanLocked, setScanLocked] = useState(false);
  const unlockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Multi-scan: acumular lecturas hasta que dos consecutivas coincidan
  const lastReadRef = useRef<{ code: string; at: number }>({ code: '', at: 0 });
  const multiScanStateRef = useRef<MultiScanState>({ pendingReads: [], mismatchCount: 0 });

  // UI states
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackCode, setFallbackCode] = useState('');
  const [confirmedCode, setConfirmedCode] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);

  const resetScanState = useCallback(() => {
    multiScanStateRef.current = { pendingReads: [], mismatchCount: 0 };
    lastReadRef.current = { code: '', at: 0 };
    setShowFallback(false);
    setFallbackCode('');
    setConfirmedCode(null);
    setScanLocked(false);
    setTorchOn(false);
  }, []);

  useEffect(() => {
    if (!visible) {
      resetScanState();
    }
  }, [visible, resetScanState]);

  useEffect(() => {
    return () => {
      if (unlockTimeoutRef.current) {
        clearTimeout(unlockTimeoutRef.current);
      }
    };
  }, []);

  const acceptCode = useCallback(
    (code: string) => {
      if (requireConfirmation) {
        setConfirmedCode(code);
      } else {
        setScanLocked(true);
        setTorchOn(false);
        onCodeScanned(code);
        unlockTimeoutRef.current = setTimeout(() => {
          setScanLocked(false);
        }, SCAN_LOCK_MS);
      }
    },
    [requireConfirmation, onCodeScanned],
  );

  const handleBarcodeScanned = (result: BarcodeScanningResult) => {
    if (scanLocked || showFallback || confirmedCode !== null) {
      return;
    }

    const code = result.data.trim();
    if (!code) {
      return;
    }

    const now = Date.now();
    // Eco: mismo código muy rápido → ignorar
    if (lastReadRef.current.code === code && now - lastReadRef.current.at < INTER_READ_LOCK_MS) {
      return;
    }

    lastReadRef.current = { code, at: now };

    const scanResult = processScanRead(multiScanStateRef.current, code, MAX_MISMATCHES);
    multiScanStateRef.current = scanResult.state;

    if (scanResult.kind === 'accepted') {
      acceptCode(scanResult.code);
    } else if (scanResult.kind === 'fallback') {
      setShowFallback(true);
      setFallbackCode(scanResult.code);
    }
    // kind === 'pending' → no hacer nada, esperar siguiente lectura
  };

  const handleConfirmCode = useCallback(() => {
    if (confirmedCode !== null) {
      setScanLocked(true);
      setTorchOn(false);
      onCodeScanned(confirmedCode);
      unlockTimeoutRef.current = setTimeout(() => {
        setScanLocked(false);
      }, SCAN_LOCK_MS);
      setConfirmedCode(null);
    }
  }, [confirmedCode, onCodeScanned]);

  const handleRescan = useCallback(() => {
    multiScanStateRef.current = { pendingReads: [], mismatchCount: 0 };
    lastReadRef.current = { code: '', at: 0 };
    setShowFallback(false);
    setFallbackCode('');
    setConfirmedCode(null);
  }, []);

  const handleUseFallbackCode = useCallback(() => {
    acceptCode(fallbackCode);
  }, [acceptCode, fallbackCode]);

  const toggleTorch = useCallback(() => {
    setTorchOn((prev) => !prev);
  }, []);

  const permissionBlocked = permission && !permission.granted;

  const overlayMessage = useMemo(() => {
    if (scanLocked) return 'Código leído.';
    if (multiScanStateRef.current.pendingReads.length === 1) return 'Leyendo... mantené el código quieto.';
    return 'Poné el código dentro del recuadro';
  }, [scanLocked]);

  const permissionBody = useMemo(() => {
    if (!permission) {
      return (
        <View style={[styles.stateCard, { minHeight: stateCardMinHeight }]}>
          <ActivityIndicator color={ticketColors.gold} />
          <Text style={styles.stateTitle}>Abriendo cámara...</Text>
        </View>
      );
    }

    if (permissionBlocked) {
      return (
        <View style={[styles.stateCard, { minHeight: stateCardMinHeight }]}>
          <Text style={styles.stateTitle}>Hace falta permiso para usar la cámara</Text>
          <Text style={styles.stateSubtitle}>Sin cámara no se puede leer el código.</Text>
          <TouchableOpacity style={styles.primaryButton} onPress={() => requestPermission()}>
            <Text style={styles.primaryButtonText}>Permitir cámara</Text>
          </TouchableOpacity>
        </View>
      );
    }

    // Fallback: 3 mismatches → mostrar último código
    if (showFallback) {
      return (
        <View style={[styles.stateCard, { minHeight: stateCardMinHeight }]}>
          <Ionicons name="warning-outline" size={40} color={ticketColors.gold} />
          <Text style={styles.stateTitle}>No pudimos leer el código dos veces seguidas</Text>
          <Text style={styles.codeDisplay}>{fallbackCode}</Text>
          <Text style={styles.stateSubtitle}>Si lo ves bien, usalo. Si no, volvé a escanear.</Text>
          <View style={styles.fallbackActions}>
            <TouchableOpacity style={styles.secondaryButton} onPress={handleRescan}>
              <Text style={styles.secondaryButtonText}>Re-scannear</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.primaryButton} onPress={handleUseFallbackCode}>
              <Text style={styles.primaryButtonText}>Usar este código</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    // Confirmación visual: mostrar código aceptado
    if (confirmedCode !== null) {
      return (
        <View style={[styles.stateCard, { minHeight: stateCardMinHeight }]}>
          <Ionicons name="checkmark-circle-outline" size={40} color={ticketColors.gold} />
          <Text style={styles.stateTitle}>Código leído</Text>
          <Text style={styles.codeDisplay}>{confirmedCode}</Text>
          <Text style={styles.stateSubtitle}>Confirmá que el código sea correcto.</Text>
          <View style={styles.fallbackActions}>
            <TouchableOpacity style={styles.secondaryButton} onPress={handleRescan}>
              <Text style={styles.secondaryButtonText}>Re-scannear</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.primaryButton} onPress={handleConfirmCode}>
              <Text style={styles.primaryButtonText}>Confirmar</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    // Camera activa
    return (
      <View>
        <View style={styles.cameraToolbar}>
          <TouchableOpacity style={styles.torchButton} onPress={toggleTorch}>
            <Ionicons name={torchOn ? 'flash' : 'flash-off'} size={22} color={torchOn ? ticketColors.gold : ticketColors.ink2} />
            <Text style={styles.torchLabel}>{torchOn ? 'Linterna on' : 'Linterna'}</Text>
          </TouchableOpacity>
        </View>
        <View style={[styles.cameraFrame, { height: cameraFrameHeight }]}>
          <CameraView
            style={styles.camera}
            facing="back"
            enableTorch={torchOn}
            onBarcodeScanned={visible ? handleBarcodeScanned : undefined}
            barcodeScannerSettings={{ barcodeTypes: barcodeTypes as any }}
          />
          <View style={styles.overlay}>
            <View style={styles.scanGuide} />
            <Text style={styles.overlayText}>{overlayMessage}</Text>
          </View>
        </View>
      </View>
    );
  }, [
    cameraFrameHeight,
    confirmedCode,
    fallbackCode,
    handleConfirmCode,
    handleRescan,
    handleUseFallbackCode,
    overlayMessage,
    permission,
    permissionBlocked,
    requestPermission,
    scanLocked,
    showFallback,
    stateCardMinHeight,
    torchOn,
    toggleTorch,
    visible,
  ]);

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={handleDeferredClose}>
      <Pressable style={[styles.backdrop, isMobile ? { justifyContent: 'flex-start', paddingTop: Math.max(insets.top, spacing.md) } : null, isMobile ? null : modalBackdropPadding]} onPress={isMobile ? handleDeferredClose : undefined} accessibilityLabel="Cerrar escáner">
        <Pressable style={[isMobile ? [styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }] : styles.modalCard]} onPress={(e) => e.stopPropagation()}>
          {isMobile ? <View style={styles.grabber} /> : null}
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>{title}</Text>
              <Text style={styles.subtitle}>{subtitle}</Text>
            </View>
          </View>

          {permissionBody}

          <View style={styles.actions}>
            <TouchableOpacity style={styles.secondaryButton} onPress={handleDeferredClose}>
              <Text style={styles.secondaryButtonText}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
  },
  sheetCard: {
    width: '100%',
    maxHeight: '92%',
    backgroundColor: ticketColors.paper2,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.md,
    gap: spacing.md,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
  modalCard: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.md,
    gap: spacing.md,
  },
  header: {
    gap: spacing.xs,
  },
  title: {
    ...typography.titleXL,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  subtitle: {
    ...typography.body,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  webHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: spacing.sm,
  },
  stateCard: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.lg,
  },
  stateTitle: {
    ...typography.titleLg,
    color: ticketColors.ink,
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
  },
  stateSubtitle: {
    ...typography.body,
    color: ticketColors.ink2,
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
  },
  codeDisplay: {
    ...typography.titleXL,
    color: ticketColors.ink,
    fontFamily: ticketFonts.mono,
    fontSize: 28,
    letterSpacing: 2,
    textAlign: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: 10,
    backgroundColor: ticketColors.dim,
  },
  fallbackActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  cameraFrame: {
    borderRadius: 20,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: '#000000',
  },
  camera: {
    flex: 1,
  },
  overlay: {
    ...StyleSheet.absoluteFill,
    pointerEvents: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    backgroundColor: 'transparent',
    gap: spacing.md,
  },
  scanGuide: {
    width: '85%',
    height: 140,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: ticketColors.gold,
    backgroundColor: 'transparent',
  },
  overlayText: {
    ...typography.body,
    color: ticketColors.ink,
    textAlign: 'center',
    fontWeight: '600',
  },
  cameraToolbar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: spacing.xs,
  },
  torchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  torchLabel: {
    ...typography.button,
    color: ticketColors.ink2,
    fontSize: 13,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  primaryButton: {
    borderRadius: 14,
    backgroundColor: ticketColors.gold,
    paddingHorizontal: spacing.lg,
    paddingVertical: 10,
  },
  primaryButtonText: {
    ...typography.button,
    color: ticketColors.goldInk,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  secondaryButton: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.lg,
    paddingVertical: 10,
  },
  secondaryButtonText: {
    ...typography.button,
    color: ticketColors.ink,
  },
});
